import React from 'react';
import { FOCUS_RING_INSET } from './focusRing';
import { isRenderableNode } from './renderableNode';
import {
    FOREGROUND_RESET,
    SELECTED_ROW_ACCENT,
    SELECTED_ROW_BACKGROUND,
    SELECTED_ROW_FOREGROUND,
    SELECTED_TEXT_CONTEXT,
} from './selectedRow';

export interface SurfaceProps {
    children?: React.ReactNode;
    padding?: boolean;
    /**
     * Content overflow. Defaults to `hidden` so children are clipped to the rounded
     * corners. Use `clip` to keep the rounded clipping WITHOUT creating a scroll
     * container — so a `position: sticky` descendant still pins (unlike `hidden`,
     * which traps it). `visible` disables clipping entirely.
     */
    overflow?: 'hidden' | 'visible' | 'clip';
    role?: React.AriaRole;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    className?: string;
    style?: React.CSSProperties;
}

export interface SurfaceRowProps {
    children?: React.ReactNode;
    /** Renders a bottom divider — omit on the last row in a Surface. */
    divider?: boolean;
    /** Removes the hover affordance / disables the control (e.g. while loading/busy). */
    disabled?: boolean;
    /**
     * When provided, the whole row becomes clickable: it renders as a `<button>`
     * (or `<a>` when `href` is set), so the entire row is the click target with
     * native focus + Enter/Space — no manual tabIndex/key handlers. For a
     * selectable row, put a presentational `<Radio>`/`<Checkbox>` inside as the
     * visual indicator; do NOT nest an interactive control (invalid + double tab stop).
     */
    onClick?: (event: React.MouseEvent) => void;
    /** Renders the clickable row as an anchor instead of a button. */
    href?: string;
    /** Announces the row as selected — `aria-checked` under a `radio`/`checkbox` role,
     *  `aria-current` otherwise — and paints `Table`'s selected treatment unless `selection`
     *  says not to. */
    selected?: boolean;
    /** How a `selected` row shows it. `highlight` paints the treatment; `announce` leaves the
     *  visual to a control inside the row and only sets the ARIA state. Defaults to `announce`
     *  under `role="checkbox"` and `highlight` otherwise. */
    selection?: 'highlight' | 'announce';
    /** Content pinned right, outside the row button, so it may hold its own control. */
    trailing?: React.ReactNode;
    /** `muted` tints a row whose subject is inactive — not disabled: it stays interactive. */
    tone?: 'default' | 'muted';
    /** ARIA role for a clickable row, e.g. `radio`/`checkbox` (wrap the list in a `radiogroup`). */
    role?: React.AriaRole;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    className?: string;
    style?: React.CSSProperties;
}

const TRIPLE_SHADOW =
    '0px 0px 4px 0px rgba(0,0,0,0.08), 0px 4px 4px 0px rgba(0,0,0,0.08), 0px 4px 10px 0px rgba(0,0,0,0.05)';

/**
 * A single full-bleed row inside a <Surface>. Owns the row chrome — hover
 * highlight, colour transition and an optional bottom divider — so consumers
 * compose their own content inside without re-implementing design-system styling.
 * Pass `onClick`/`href` to make the entire row clickable (rendered as a real
 * button/anchor, so keyboard navigation works natively).
 */
const SurfaceRow: React.FC<SurfaceRowProps> = ({
    children,
    divider = false,
    disabled = false,
    onClick,
    href,
    selected,
    selection,
    trailing,
    tone = 'default',
    role,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    className = '',
    style,
}) => {
    const interactive = Boolean(onClick || href);
    const hover =
        disabled && interactive
            ? ''
            : 'transition-colors hover:bg-[var(--malachite-buttonlist-hover-background)]';

    // A class, not an inline colour, which would outrank the hover utility. It replaces the
    // button reset's `bg-transparent` rather than joining it: same specificity, so the winner
    // would be whichever Tailwind emitted last.
    const muted = tone === 'muted';
    const toneClass = muted ? 'bg-[var(--malachite-card-dimmed-background)]' : '';

    const chromeClasses = [
        'w-full text-left border-0',
        toneClass,
        divider ? 'border-b border-solid border-[var(--malachite-card-border)]' : '',
        hover,
    ]
        .filter(Boolean)
        .join(' ');

    const controlClasses = [
        interactive
            ? `appearance-none ${muted ? '' : 'bg-transparent'} p-0 ${FOCUS_RING_INSET}`
            : '',
        interactive && !disabled ? 'cursor-pointer' : '',
    ]
        .filter(Boolean)
        .join(' ');

    const classes = [chromeClasses, controlClasses, className].filter(Boolean).join(' ');

    const paints = selection ? selection === 'highlight' : role !== 'checkbox';

    // A gradient layer so the tint composites over hover, and an inset shadow because
    // `ring-*` silently doesn't paint in a shadow root.
    const selectedStyle: React.CSSProperties =
        selected && paints
            ? {
                  backgroundImage: `linear-gradient(${SELECTED_ROW_BACKGROUND}, ${SELECTED_ROW_BACKGROUND})`,
                  color: SELECTED_ROW_FOREGROUND,
                  boxShadow: `inset 2px 0 0 ${SELECTED_ROW_ACCENT}`,
                  ...SELECTED_TEXT_CONTEXT,
              }
            : {};

    const rowStyle = { ...selectedStyle, ...style };

    const ariaChecked = role === 'radio' || role === 'checkbox' ? Boolean(selected) : undefined;
    // `aria-current`, not `aria-pressed`: a selected row is one of many choices, not a toggle
    // (same call as `Dropdown`'s options). A role that carries its own state uses that instead.
    const ariaCurrent = ariaChecked === undefined && selected ? true : undefined;

    // Everything a control would have carried, for the branches that render none: the row
    // element is then the only thing assistive tech sees. `aria-disabled` because there is no
    // control to be natively disabled — and it is inert without a role, like the name.
    const rowFallbackAria = {
        role,
        'aria-checked': ariaChecked,
        'aria-current': ariaCurrent,
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        'aria-disabled': role && disabled ? true : undefined,
    };

    if (isRenderableNode(trailing)) {
        const control =
            href && !disabled ? (
                <a
                    href={href}
                    onClick={onClick}
                    className={`${controlClasses} border-0 flex-1 min-w-0 text-left no-underline text-inherit`}
                    role={role}
                    aria-checked={ariaChecked}
                    aria-current={ariaCurrent}
                    aria-label={ariaLabel}
                    aria-labelledby={ariaLabelledBy}
                >
                    {children}
                </a>
            ) : onClick ? (
                <button
                    type="button"
                    onClick={onClick}
                    disabled={disabled}
                    className={`${controlClasses} border-0 flex-1 min-w-0 text-left`}
                    role={role}
                    aria-checked={ariaChecked}
                    aria-current={ariaCurrent}
                    aria-label={ariaLabel}
                    aria-labelledby={ariaLabelledBy}
                >
                    {children}
                </button>
            ) : (
                // The set goes here, not on the wrapper: `radio` and `checkbox` make their
                // descendants presentational, which would bury a control in the trailing slot.
                <div className="flex-1 min-w-0" {...rowFallbackAria}>
                    {children}
                </div>
            );

        return (
            <div
                className={[chromeClasses, 'flex items-center', className]
                    .filter(Boolean)
                    .join(' ')}
                style={rowStyle}
            >
                {control}
                <div className="shrink-0 flex items-center">{trailing}</div>
            </div>
        );
    }

    if (href && !disabled) {
        return (
            <a
                href={href}
                onClick={onClick}
                className={`${classes} no-underline text-inherit`}
                style={rowStyle}
                role={role}
                aria-checked={ariaChecked}
                aria-current={ariaCurrent}
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
            >
                {children}
            </a>
        );
    }

    if (onClick) {
        return (
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                className={classes}
                style={rowStyle}
                role={role}
                aria-checked={ariaChecked}
                aria-current={ariaCurrent}
                aria-label={ariaLabel}
                aria-labelledby={ariaLabelledBy}
            >
                {children}
            </button>
        );
    }

    return (
        <div className={classes} style={rowStyle} {...rowFallbackAria}>
            {children}
        </div>
    );
};

interface SurfaceComponent extends React.FC<SurfaceProps> {
    Row: React.FC<SurfaceRowProps>;
}

const Surface = (({
    children,
    padding = false,
    overflow = 'hidden',
    role,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    className = '',
    style,
}: SurfaceProps) => {
    // Literal class names (not interpolated) so Tailwind's JIT emits them.
    const overflowClass =
        overflow === 'clip'
            ? 'overflow-clip'
            : overflow === 'visible'
              ? 'overflow-visible'
              : 'overflow-hidden';
    return (
        <div
            className={`w-full rounded-[var(--malachite-radius-xxl,14px)] ${overflowClass} bg-[var(--malachite-card-background)] border border-solid border-[var(--malachite-card-border)] ${padding ? 'py-3 px-4' : ''} ${className}`}
            style={{ boxShadow: TRIPLE_SHADOW, ...FOREGROUND_RESET, ...style }}
            role={role}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
        >
            {children}
        </div>
    );
}) as SurfaceComponent;

Surface.Row = SurfaceRow;

export default Surface;
export { Surface, SurfaceRow };
